import { useCallback, useRef, useState } from 'react';

/**
 * useScrub — pointer-capture progress scrubbing with keyboard seeking.
 * Technique from the Aug-9 hero scrub bars (pointer capture so the drag never
 * escapes the handle) generalized for any CCPlayer preset.
 *
 * The caller renders a track element with `trackRef` and spreads
 * `scrubHandlers` on the interactive slider element. Live seeking: the video's
 * currentTime is written continuously during the drag (MediaViewer behavior).
 *
 * @param videoRef  ref to the <video>
 * @param duration  metadata duration (from useVideoEngine)
 * @param onScrubEnd optional callback after a completed drag (analytics)
 */
const useScrub = (videoRef, duration, onScrubEnd) => {
  const trackRef = useRef(null);
  const scrubbingRef = useRef(false);
  const [scrubbing, setScrubbing] = useState(false);

  const seekToClientX = useCallback((clientX) => {
    const v = videoRef.current;
    const track = trackRef.current;
    if (!v || !track || !duration) return;
    const r = track.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    v.currentTime = frac * duration;
  }, [videoRef, duration]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    scrubbingRef.current = true;
    setScrubbing(true);
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    seekToClientX(e.clientX);
  }, [seekToClientX]);

  const onPointerMove = useCallback((e) => {
    if (scrubbingRef.current) seekToClientX(e.clientX);
  }, [seekToClientX]);

  const onPointerUp = useCallback(() => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    setScrubbing(false);
    if (onScrubEnd) onScrubEnd();
  }, [onScrubEnd]);

  const onKeyDown = useCallback((e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    v.currentTime = Math.min(duration, Math.max(0, v.currentTime + (e.key === 'ArrowRight' ? 5 : -5)));
  }, [videoRef, duration]);

  return {
    trackRef,
    scrubbing,
    scrubHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
    },
  };
};

export default useScrub;
